import { captureException } from '@sentry/remix'
import { forwardRef } from 'react'
import type { FC } from 'react'
import Skeleton from 'react-loading-skeleton'
import { CourseList } from '~/components/course/CourseList'
import { NoCourse } from '~/components/course/NoCourse'
import {
  FilterChips,
  useBaseSetApplyPriceRange,
  useClear,
  useSetBuffet,
  useSetChildUse,
  useSetSeatAttributes,
  useSetSmokingAllowed,
} from '~/components/filterChips'
import {
  NO_COURSE_TEXT,
  NO_INVENTORY_CURRENT_CRITERIA,
  NO_INVENTORY_TEXT,
} from '~/lib/constants/constants'
import { SystemError } from '~/lib/error/systemError'
import type { RestaurantId } from '~/lib/graphql/scalars'
import type { GuideQuery } from '~/lib/query/guide'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import {
  useAfternoonTea,
  useBuffet,
  useChildUse,
  useChristmas,
  useEnabledAfternoonTea,
  useEnabledBuffet,
  useEnabledChildUses,
  useEnabledChristmas,
  useEnabledFreeFlows,
  useEnabledSeatAttributes,
  useEnabledSmokingAllowed,
  useFilterCriteria,
  useFilteredCourses,
  useFreeFlows,
  useInitialPriceRange,
  useSeatAttributes,
  useSetAfternoonTea,
  useSetChristmas,
  useSetFreeFlows,
  useShow,
  useSmokingAllowed,
} from './courseList'
import { useCourseSection, useRefTitle } from './hooks'

type Props = {
  restaurantId: RestaurantId
  query: GuideQuery
  hideClear?: boolean
  defaultOpen?: boolean
}

const courseSectionStyles = sva({
  slots: ['section', 'heading', 'container', 'title', 'charge'],
  base: {
    section: {
      spaceY: '3',
      pt: '4',
      _pc: {
        spaceY: '5',
        paddingY: '0',
      },
    },
    container: {
      spaceY: '3',
      _pc: {
        spaceY: '4',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      paddingX: '3',
      _pc: {
        paddingX: '0',
      },
    },
    title: {
      fontSize: '2xl',
      fontWeight: 'bold',
      _pc: {
        fontSize: '3xl',
      },
    },
    charge: {
      marginLeft: 'auto',
      fontSize: 'xs',
      color: 'gray.600',
      _pc: {
        fontSize: 'sm',
      },
    },
  },
})

const classes = courseSectionStyles()

// FIXME py-4 は外部から与える
export function CourseSection({ restaurantId, query, hideClear, defaultOpen = false }: Props) {
  const { data, isPending, error, variables, seatOnlyPlans, hasSearchInput } = useCourseSection()
  const refTitle = useRefTitle()
  const courses = useFilteredCourses()
  const isPc = useIsPC()

  if (error !== null) {
    captureException(new Error(error.message + ' variables : ' + JSON.stringify(variables)))
    return null
  }

  if (isPending || data === undefined) {
    return <CourseSectionSkeleton />
  }

  if (!data.restaurant) {
    captureException(new SystemError('レストラン情報が取得できませんでした。'))
    return null
  }

  const reservable = courses.length > 0 || seatOnlyPlans.length > 0
  const noCourseMainText = reservable
    ? NO_COURSE_TEXT.main
    : hasSearchInput
      ? NO_INVENTORY_CURRENT_CRITERIA
      : NO_INVENTORY_TEXT

  const noCourseSubText = reservable ? NO_COURSE_TEXT.sub : undefined

  return (
    <section className={classes.section} id="plans">
      <CourseSectionHeading ref={refTitle} />
      {reservable ? (
        <div className={classes.container}>
          <FilterChips
            useFilterCriteria={useFilterCriteria}
            useFreeFlows={useFreeFlows}
            useSetFreeFlows={useSetFreeFlows}
            useEnabledFreeFlows={useEnabledFreeFlows}
            useAfternoonTea={useAfternoonTea}
            useSetAfternoonTea={useSetAfternoonTea}
            useEnabledAfternoonTea={useEnabledAfternoonTea}
            useBuffet={useBuffet}
            useSetBuffet={useSetBuffet}
            useEnabledBuffet={useEnabledBuffet}
            useSmokingAllowed={useSmokingAllowed}
            useSetSmokingAllowed={useSetSmokingAllowed}
            useEnabledSmokingAllowed={useEnabledSmokingAllowed}
            useSeatAttributes={useSeatAttributes}
            useSetSeatAttributes={useSetSeatAttributes}
            useEnabledSeatAttributes={useEnabledSeatAttributes}
            useChildUse={useChildUse}
            useSetChildUse={useSetChildUse}
            useEnabledChildUse={useEnabledChildUses}
            useApplyPrice={useBaseSetApplyPriceRange}
            useInitialPriceRange={useInitialPriceRange}
            useChristmas={useChristmas}
            useEnabledChristmas={useEnabledChristmas}
            useSetChristmas={useSetChristmas}
            useClear={useClear}
            useShow={useShow}
          />
          <CourseList
            courses={courses}
            seatOnlyPlans={seatOnlyPlans}
            restaurantId={restaurantId}
            restaurant={data.restaurant}
            query={query}
            hideClear={hideClear}
            defaultOpen={defaultOpen}
          />
        </div>
      ) : (
        <NoCourse
          restaurantId={restaurantId}
          withClearButton={(reservable || hasSearchInput) && !isPc}
          mainText={noCourseMainText}
          subText={noCourseSubText}
        />
      )}
    </section>
  )
}

// FIXME px-3 は外部から与える
const IkyuCourseSectionHeading = forwardRef<HTMLHeadingElement>((_, ref) => (
  <div className={classes.heading}>
    <h2 ref={ref} className={classes.title}>
      レストラン予約
    </h2>
    <div className={classes.charge}>お一人様 消費税・サービス料込</div>
  </div>
))

// FIXME px-3 は外部から与える
const PpgCourseSectionHeading = forwardRef<HTMLHeadingElement>((_, ref) => (
  <div className={classes.heading}>
    <h2 ref={ref} className={classes.title}>
      コース一覧
    </h2>
    <div className={classes.charge}>お一人様 消費税・サービス料込</div>
  </div>
))

const CourseSectionHeading =
  import.meta.env.VITE_MODE === 'ppg' ? PpgCourseSectionHeading : IkyuCourseSectionHeading

const skeletonStyles = sva({
  slots: ['container', 'heading', 'title', 'content', 'charge'],
  base: {
    container: {
      columnGap: '3',
      paddingTop: '4',
      _pc: {
        paddingY: '0',
        spaceY: '5',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      _sd: {
        px: '3',
      },
    },
    title: {
      fontSize: '2xl',
      fontWeight: 'bold',
      _sd: {
        pb: '3',
      },
      _pc: {
        fontSize: '3xl',
      },
    },
    charge: {
      marginLeft: 'auto',
      fontSize: 'xs',
      color: 'gray.600',
      _pc: {
        fontSize: 'sm',
      },
    },
    content: {
      _sd: {
        p: '3',
        pb: '5',
        bg: 'gray.100',
      },
      spaceY: { base: '2', _pc: '3' },
    },
  },
})

function CourseSectionSkeleton() {
  const classes = skeletonStyles()
  const isPC = useIsPC()
  const SkeletonCard = isPC ? PcSkeletonCard : SpSkeletonCard
  return (
    <section className={classes.container}>
      <CourseSectionSkeletonHeading />
      <div className={classes.content}>
        <SkeletonCard />
        <SkeletonCard />
        {!isPC && <SkeletonCard />}
      </div>
    </section>
  )
}

const pcSkeletonCardStyles = sva({
  slots: ['container', 'heading', 'title', 'list', 'item'],
  base: {
    container: {
      spaceY: '4',
    },
    item: {
      borderRadius: 'md',
      border: 'medium',
      p: '4',
      spaceY: '4',
    },
    heading: {
      display: 'flex',
      flex: '1',
      alignItems: 'start',
      justifyContent: 'start',
      columnGap: '3',
      pb: '3',
    },
    title: {
      display: 'flex',
      flexDirection: 'column',
      spaceY: '2',
      columnGap: '3',
    },
    list: {
      pt: '4',
      borderTop: 'dotted',
    },
  },
})

function PcSkeletonCard() {
  const classes = pcSkeletonCardStyles()
  return (
    <>
      <div className={classes.item}>
        <div className={classes.heading}>
          <Skeleton height={80} width={114} />
          <div className={classes.title}>
            <Skeleton height={14} width={295} />
            <Skeleton height={16} width={98} />
          </div>
        </div>
        <div className={classes.list}>
          <Skeleton height={70} width={588} />
        </div>
      </div>
    </>
  )
}

function SpSkeletonCard() {
  return (
    <div className={css({ border: 'medium', rounded: 'default', bg: 'white' })}>
      <div className={css({ p: '3', pb: '2', display: 'flex', columnGap: '2' })}>
        <Skeleton height={68} width={96} />
        <div className={css({ display: 'flex', flexDirection: 'column' })}>
          <Skeleton height={14} width={223} />
          <div className={css({ mt: 'auto', textAlign: 'right' })}>
            <Skeleton height={13} width={76} />
          </div>
        </div>
      </div>
      <div className={css({ textAlign: 'center', borderTop: 'light', py: '2' })}>
        <Skeleton height={12} width={150} />
      </div>
    </div>
  )
}

const CourseSectionSkeletonHeading: FC =
  import.meta.env.VITE_MODE === 'ppg'
    ? PpgCourseSectionSkeletonHeading
    : IkyuCourseSectionSkeletonHeading

function IkyuCourseSectionSkeletonHeading() {
  const classes = skeletonStyles()
  return (
    <div className={classes.heading}>
      <h2 className={classes.title}>レストラン予約</h2>
      <div className={classes.charge}>お一人様 消費税・サービス料込</div>
    </div>
  )
}

function PpgCourseSectionSkeletonHeading() {
  const classes = skeletonStyles()
  return (
    <div className={classes.heading}>
      <h2 className={classes.title}>コース一覧</h2>
      <div className={classes.charge}>お一人様 消費税・サービス料込</div>
    </div>
  )
}
